import { Controller } from "@hotwired/stimulus"

// Generic client-side table sort for a server-rendered table whose rows are all on the page.
//
//   <div data-controller="table-sort">
//     <table>
//       <thead><tr><th>
//         <button data-table-sort-target="header" data-action="table-sort#sort"
//                 data-table-sort-column-param="0" data-table-sort-first-param="asc">
//           Name<span data-table-sort-target="arrow"></span>
//         </button>
//       </th>…</tr></thead>
//       <tbody data-table-sort-target="body">
//         <tr><td data-sort-value="Ada">Ada</td>…</tr>
//
// Each cell carries `data-sort-value`, so a formatted cell ("92.3% (12/13)", "$1,204") sorts on the
// value behind it rather than on its text. A value that parses as a number sorts numerically,
// otherwise it is compared with localeCompare. Clicking the active header reverses it; a header's
// first click uses its `first` param ("asc" / "desc").
export default class extends Controller {
  static targets = ["body", "header", "arrow"]
  static values = { column: Number, direction: String }

  sort(event) {
    event.preventDefault()
    const { column, first } = event.params
    const direction =
      this.hasColumnValue && this.columnValue === column
        ? (this.directionValue === "asc" ? "desc" : "asc")
        : (first === "desc" ? "desc" : "asc")

    this.columnValue = column
    this.directionValue = direction
    this.apply(column, direction)
    this.showArrows(column, direction)
  }

  apply(column, direction) {
    if (!this.hasBodyTarget) return

    const body = this.bodyTarget
    const rows = Array.from(body.rows)
    const sign = direction === "asc" ? 1 : -1

    rows.sort((a, b) => {
      const left = this.cellValue(a, column)
      const right = this.cellValue(b, column)
      if (typeof left === "number" && typeof right === "number") return (left - right) * sign
      return String(left).localeCompare(String(right)) * sign
    })

    rows.forEach((row) => body.appendChild(row))
  }

  cellValue(row, column) {
    const cell = row.cells[column]
    if (!cell) return ""
    const raw = cell.dataset.sortValue !== undefined ? cell.dataset.sortValue : cell.textContent.trim()
    if (raw === "") return ""
    const number = Number(raw)
    return Number.isNaN(number) ? raw : number
  }

  showArrows(column, direction) {
    this.arrowTargets.forEach((arrow) => { arrow.textContent = "" })
    this.headerTargets.forEach((header) => {
      // aria-sort belongs on the <th scope="col">, not on the button inside it -- screen readers
      // ignore it anywhere else. The header target IS the button, so walk up to its cell.
      const cell = header.closest("th") || header
      cell.removeAttribute("aria-sort")
      if (Number(header.dataset.tableSortColumnParam) !== column) return

      cell.setAttribute("aria-sort", direction === "asc" ? "ascending" : "descending")
      const arrow = header.querySelector("[data-table-sort-target='arrow']")
      if (arrow) arrow.textContent = direction === "asc" ? "↑" : "↓"
    })
  }
}
